'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import ShowingTagCover from '@/app/[locale]/(default-layout)/specials/_components/ShowingTagCover';

import BellSubscribeButton from '@/components/newsletter/BellSubscribeButton';
import SkeletonView from '@/components/skeleton-view/SkeletonView';

import { ShowingTagDto } from '@cineamo/api/models/showing-tag/ShowingTagDto.types';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useFutureShowings } from '@/hooks/useFutureShowings';

import { IconTicketSlanted24 } from '@cineamo/ui/src/icons/ic-ticketSlanted';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type ShowingTagCardProps = {
    showingTag: ShowingTagDto;
};

function ShowingTagCard(props: ShowingTagCardProps): React.JSX.Element {
    const { showingTag } = props;
    const domainConfiguration = useDomainConfiguration();
    const t = useTranslations();

    const cinema = domainConfiguration.cinema;
    const cinemaId = cinema.id;

    const { data: showingsResponse, isLoading: isLoadingShowings } = useFutureShowings(cinemaId, null, {
        showingTagIds: [showingTag.id]
    });

    return (
        <Link
            className={cn(
                'flex flex-col w-full h-min rounded-8 overflow-hidden border-2 border-[var(--surface-color)] cursor-pointer group'
            )}
            draggable={false}
            href={
                {
                    pathname: '/specials/[showingTagId]',
                    params: {
                        showingTagId: showingTag.id
                    }
                } as HrefOrHrefWithParams<string>
            }
            /*
             * Prefetch the full page data beyond the loading.js boundaries by setting the prefetch prop to true
             * (See: https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#2-prefetching)
             */
            aria-label={showingTag.name}
            prefetch={false}>
            <div className="relative w-full h-auto aspect-[16/9] overflow-hidden">
                <BellSubscribeButton
                    topic={{
                        type: 'showing_tag',
                        referenceId: String(showingTag.id),
                        label: showingTag.name
                    }}
                    className="absolute top-8 right-8"
                />
                <ShowingTagCover
                    showingTag={showingTag}
                    showingsCount={showingsResponse?.length}
                />
            </div>
            <div className="flex flex-row h-64 px-12 py-8 space-x-12 items-center text-[var(--on-surface-color)]">
                <div className="flex-auto flex flex-col space-y-4">
                    <span className="text-body-bold-14">{showingTag.name}</span>
                    {isLoadingShowings ? (
                        <SkeletonView className="h-[21px]" />
                    ) : (
                        <span className="text-body-bold-12 text-[var(--secondary-color,var(--on-surface-color))]">
                            {t('specials.n-showings', {
                                count: showingsResponse?.length ?? '-'
                            })}
                        </span>
                    )}
                </div>
                <IconTicketSlanted24 className="flex-none" />
            </div>
        </Link>
    );
}

export default ShowingTagCard;
